<template>
  <div
    v-if="details"
    class="sidebar-profile">
    <q-btn
      flat
      no-caps
      class="sidebar-profile__btn">
      <div class="sidebar-profile__row">
        <q-avatar
          rounded
          size="32px"
          color="primary"
          text-color="white"
          class="sidebar-profile__avatar">
          {{ initials }}
        </q-avatar>
        <div class="sidebar-profile__info">
          <div class="sidebar-profile__name">{{ details.full_name }}</div>
          <div class="sidebar-profile__role">{{ primaryRoleLabel }}</div>
        </div>
        <div
          v-if="$slots.actions"
          class="sidebar-profile__actions"
          @click.stop>
          <slot name="actions" />
        </div>
        <q-btn
          flat
          dense
          round
          size="sm"
          class="sidebar-profile__theme"
          :icon="symRoundedSettings"
          :to="{ path: '/settings' }"
          @click.stop>
          <q-tooltip>Paramètres d'application</q-tooltip>
        </q-btn>
      </div>
      <q-menu
        transition-show="jump-up"
        transition-hide="jump-down">
        <ProfileCard class="q-ma-none" />
      </q-menu>
    </q-btn>
  </div>
</template>

<script lang="ts" setup>
import { symRoundedSettings } from '@quasar/extras/material-symbols-rounded'
import { useUserStore } from 'src/CrudManager/Core/stores/useUserStore'
import { getInitials } from 'src/CrudManager/Core/Utils/textFormatters'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import ProfileCard from './ProfileCard.vue'

const userStore = useUserStore()
const { t } = useI18n()

const details = computed(() => userStore.user)
const initials = computed(() => (details.value ? getInitials(details.value.full_name) : ''))
const primaryRoleLabel = computed(() => {
  const firstRole = details.value?.roles?.[0]
  if (!firstRole) return ''
  return t(`crudmodels.user.roles.${firstRole}`)
})
</script>

<style lang="scss" scoped>
.sidebar-profile {
  padding: 8px 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.body--light .sidebar-profile {
  border-top-color: rgba(0, 0, 0, 0.06);
}

.sidebar-profile__btn {
  width: 100%;
  padding: 4px;
  border-radius: 10px;

  :deep(.q-btn__content) {
    width: 100%;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.04);
  }
}

.body--light .sidebar-profile__btn:hover {
  background: rgba(0, 0, 0, 0.03);
}

.sidebar-profile__row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}

.sidebar-profile__avatar {
  font-weight: 600;
  font-size: 0.75rem;
  flex-shrink: 0;
}

.sidebar-profile__info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
  flex: 1 1 auto;
  min-width: 0;
}

.sidebar-profile__name {
  font-size: 0.781rem;
  font-weight: 550;
  line-height: 1.15;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-profile__role {
  font-size: 0.68rem;
  color: $grey-6;
  line-height: 1.2;
  margin-top: 1px;
}

.sidebar-profile__theme {
  flex-shrink: 0;
  opacity: 0.7;
  transition: opacity 0.15s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    opacity: 1;
  }
}
</style>
